import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Typography, Input, Button, Space, Table, Tag, message, Popconfirm, Alert, Empty,
} from 'antd';
import {
  LinkOutlined, CopyOutlined, ReloadOutlined, SearchOutlined, DeleteOutlined, ExportOutlined,
} from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text, Paragraph } = Typography;

function copy(text, what = 'Link') {
  if (!text) return;
  navigator.clipboard.writeText(text)
    .then(() => message.success(`${what} copied`))
    .catch(() => message.error('Copy nahi hua — link manually copy karein'));
}

export default function ShortLinks({ user, embedded }) {
  const isAdmin = ['admin', 'super_admin', 'manager'].includes(user?.role);

  const [urls, setUrls] = useState('');
  const [creating, setCreating] = useState(false);
  const [made, setMade] = useState([]);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (search) => {
    setLoading(true);
    try {
      const res = await api.get('/short-links', { params: { q: search ?? q } });
      setRows(res.data || []);
    } catch (err) {
      message.error(err.uiMessage || 'List nahi aayi');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => { load(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    const text = urls.trim();
    if (!text) return message.warning('Link paste karo (https:// se shuru)');
    setCreating(true);
    try {
      const res = await api.post('/short-links', { urls: text });
      setMade(res.data.links || []);
      message.success(`${res.data.count} short link ban gaye`);
      if ((res.data.invalid || []).length) {
        message.warning(`${res.data.invalid.length} link theek nahi tha (https:// se shuru hona chahiye)`);
      }
      setUrls('');
      load('');
    } catch (err) {
      message.error(err.uiMessage || 'Short link nahi bana');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/short-links/${id}`);
      message.success('Hata diya — ye short link ab kaam nahi karega');
      load();
    } catch (err) {
      message.error(err.uiMessage || 'Delete fail');
    }
  };

  const columns = [
    { title: 'No.', width: 60, render: (_, __, i) => i + 1 },
    {
      title: 'Short link',
      dataIndex: 'short_url',
      width: 260,
      render: (s) => (
        <Space>
          <a href={s} target="_blank" rel="noopener noreferrer">{s}</a>
          <Button size="small" icon={<CopyOutlined />} onClick={() => copy(s, 'Short link')} />
        </Space>
      ),
    },
    {
      title: 'Asli link',
      dataIndex: 'url',
      ellipsis: true,
      render: (u) => (
        <Space>
          <a href={u} target="_blank" rel="noopener noreferrer">{u}</a>
          <Button size="small" icon={<ExportOutlined />} onClick={() => window.open(u, '_blank', 'noopener,noreferrer')} />
        </Space>
      ),
    },
    { title: 'Clicks', dataIndex: 'clicks', width: 90, render: (c) => <Tag color={c ? 'blue' : 'default'}>{c}</Tag> },
    { title: 'Kahan se', dataIndex: 'source', width: 120, render: (s) => s || '—' },
    { title: 'Bana', dataIndex: 'created_at', width: 170 },
    {
      title: '',
      width: 60,
      render: (_, r) => (isAdmin ? (
        <Popconfirm title="Ye short link hata dein? (link kaam karna band ho jayega)" onConfirm={() => remove(r.id)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ) : null),
    },
  ];

  return (
    <div>
      {!embedded && <Title level={3} style={{ marginBottom: 4 }}><LinkOutlined /> Short Links</Title>}
      <Text type="secondary">
        Koi bhi link paste karo — Instagram reel, YouTube, store link, kuch bhi — turant apna chhota link
        (bainslamusic.com/s/xxxxx) ban jayega. Click par asli link khulta hai aur clicks bhi count hote hain.
      </Text>

      <Card style={{ marginTop: 12 }}>
        <Text strong>Link (ek ya kai — har line me ek)</Text>
        <Input.TextArea
          rows={4}
          style={{ marginTop: 4 }}
          value={urls}
          onChange={(e) => setUrls(e.target.value)}
          placeholder={'https://www.instagram.com/reels/audio/2026839091588413/\nhttps://open.spotify.com/track/...'}
        />
        <Space style={{ marginTop: 12 }} wrap>
          <Button type="primary" size="large" icon={<LinkOutlined />} loading={creating} onClick={create}>
            Short link banao
          </Button>
          <Button onClick={() => { setUrls(''); setMade([]); }}>Clear</Button>
        </Space>

        {made.length > 0 && (
          <Card size="small" style={{ marginTop: 16, background: '#fafafa' }} title="Naye short link" extra={(
            <Button size="small" type="primary" icon={<CopyOutlined />} onClick={() => copy(made.map((m) => m.short_url).join('\n'), 'Saare short link')}>
              Copy all
            </Button>
          )}>
            {made.map((m) => (
              <Paragraph key={m.short_url} style={{ marginBottom: 6 }} copyable={{ text: m.short_url }}>
                <a href={m.short_url} target="_blank" rel="noopener noreferrer">{m.short_url}</a>
                <Text type="secondary" style={{ fontSize: 12 }}> ← {m.url}</Text>
              </Paragraph>
            ))}
          </Card>
        )}
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input
            placeholder="Link ya code search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onPressEnter={() => load()}
            style={{ width: 280 }}
          />
          <Button icon={<SearchOutlined />} onClick={() => load()}>Search</Button>
          <Button icon={<ReloadOutlined />} onClick={() => { setQ(''); load(''); }}>Refresh</Button>
        </Space>
        {rows.length === 0 && !loading ? (
          <Empty description="Abhi koi short link nahi bana" />
        ) : (
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={rows}
            columns={columns}
            pagination={{ pageSize: 20, showSizeChanger: false }}
          />
        )}
      </Card>

      <Alert
        style={{ marginTop: 16 }}
        type="info"
        showIcon
        message="Wahi link dobara daalo to naya code nahi banta — purana hi short link milta hai, isliye purani jagah lagaya link bhi chalta rehta hai."
      />
    </div>
  );
}
